import React, { useEffect, useState } from 'react';
import { AudioPlayer as CkAudioPlayer } from '@NP-Webber/crm-kit';
import { Badge } from '@NP-Webber/crm-kit/dist/next/index.js';
import api, { mediaUrl } from '../services/api.js';
import './screens.css';

/**
 * Small building blocks shared by the rebuilt (crm-kit/next) admin screens.
 * Everything renders crm-kit/next classes only — tokens decide the look (style × mode).
 */

/** Tabs — underline tab strip (ck-tabs). items: [{ value, label, count, disabled }] */
export function Tabs({ items = [], value, onChange, className = '', ariaLabel }) {
  return (
    <div className={`ck-tabs nx-tabs ${className}`.trim()} role="tablist" aria-label={ariaLabel}>
      {items.filter(Boolean).map((it) => (
        <button
          key={String(it.value)}
          type="button"
          role="tab"
          aria-selected={it.value === value}
          disabled={it.disabled}
          className={`ck-tab ${it.value === value ? 'is-on' : ''}`}
          onClick={() => onChange?.(it.value)}
        >
          {it.label}
          {it.count != null ? <Badge tone={it.value === value ? 'primary' : 'neutral'}>{it.count}</Badge> : null}
        </button>
      ))}
    </div>
  );
}

/** FormGrid — two columns of fields (one on narrow screens). cols: 1 | 2 | 3 | 4 */
export function FormGrid({ cols = 2, children, className = '' }) {
  return <div className={`ck-form-grid nx-grid nx-grid--${cols} ${className}`.trim()}>{children}</div>;
}

/** Row — horizontal flex with wrap. */
export function Row({ children, gap = 8, align = 'center', justify, wrap = true, className = '', style }) {
  return (
    <div className={`nx-row ${className}`.trim()} style={{ gap, alignItems: align, justifyContent: justify, flexWrap: wrap ? 'wrap' : 'nowrap', ...style }}>
      {children}
    </div>
  );
}

/** KV — label / value rows (read-only details). items: [{ label, value, ltr }] */
export function KV({ items = [] }) {
  return (
    <dl className="nx-kv">
      {items.filter(Boolean).map((it) => (
        <div key={it.label} className="nx-kv__row">
          <dt>{it.label}</dt>
          <dd dir={it.ltr ? 'ltr' : undefined}>{it.value == null || it.value === '' ? <span className="ck-muted">—</span> : it.value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** apiError — the server's message (with details) or a fallback. */
export function apiError(e, fallback = 'שגיאה') {
  const data = e?.response?.data;
  if (!data) return e?.message || fallback;
  if (Array.isArray(data.details) && data.details.length) return `${data.error || fallback}: ${data.details.join(', ')}`;
  return data.error || data.message || e?.message || fallback;
}

/**
 * AudioPlayer — crm-kit player (new-design look under the shell) fed by the bchinot API:
 *   src   endpoint under /api (streams with ?t=<token>)
 *   path  Yemot path through the recording proxy — also streamed with ?t= (no Authorization header → no CORS
 *         preflight, which some filtered networks answer without CORS headers)
 */
export function AudioPlayer({ src: srcProp, path, proxyUrl = '/members/recording-proxy', label, compact = true, onFirstPlay }) {
  const src = srcProp || (path ? `${proxyUrl}?path=${encodeURIComponent(path)}` : null);
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setUrl(null);
    if (src && !onFirstPlay) mediaUrl(src).then((u) => { if (!cancelled) setUrl(u); }).catch(() => {});
    return () => { cancelled = true; };
  }, [src, onFirstPlay]);
  // onFirstPlay — the file is fetched only on the first "play" click; that moment is reported (e.g. to mark "played")
  if (src && onFirstPlay) {
    const fetchFirst = async () => {
      const { data } = await api.get(src, { responseType: 'blob' });
      onFirstPlay();
      return data;
    };
    return <CkAudioPlayer fetchAudio={fetchFirst} label={label} compact={compact} />;
  }
  if (src) return url ? <CkAudioPlayer src={url} label={label} compact={compact} /> : <span className="ck-spinner" style={{ width: 16, height: 16 }} />;
  return null;
}

/** fmtDate — dd.mm.yyyy (Hebrew locale) or '—' */
export function fmtDate(v, withTime = false) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return withTime
    ? d.toLocaleString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Toggle — bare ck switch (for rows that already show their own label). */
export function Toggle({ checked, onChange, disabled = false, label }) {
  return (
    <input
      type="checkbox"
      role="switch"
      aria-label={label}
      title={label}
      className="ck-switch"
      checked={!!checked}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.checked, e)}
    />
  );
}

/**
 * useServerTable — state for a crm-kit DataTable in server mode.
 *   const t = useServerTable(async (query) => ({ rows, total }), [deps]);
 *   <DataTable mode="server" {...t.tableProps} … />
 * query = { page, pageSize, search, sort:{ key, dir }|null, filters }
 */
export function useServerTable(fetcher, deps = [], defaultQuery = {}) {
  const [query, setQuery] = useState(() => ({ page: 1, pageSize: 50, search: '', sort: null, filters: {}, ...defaultQuery }));
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tick, setTick] = useState(0);
  const fetchRef = React.useRef(fetcher);
  fetchRef.current = fetcher;
  const seq = React.useRef(0);
  const silentRef = React.useRef(false);
  useEffect(() => {
    const my = ++seq.current;
    if (!silentRef.current) { setLoading(true); setError(null); }
    silentRef.current = false;
    Promise.resolve(fetchRef.current(query))
      .then((res) => { if (my === seq.current) { setRows(res?.rows || []); setTotal(Number(res?.total) || 0); } })
      .catch((e) => { if (my === seq.current) setError(apiError(e, 'שגיאה בטעינה')); })
      .finally(() => { if (my === seq.current) setLoading(false); });
  }, [query, tick, ...deps]); // eslint-disable-line react-hooks/exhaustive-deps
  const reload = React.useCallback(() => setTick((n) => n + 1), []);
  /** reloadSilent — refetch without the loading state (polling) */
  const reloadSilent = React.useCallback(() => { silentRef.current = true; setTick((n) => n + 1); }, []);
  const resetPage = React.useCallback(() => setQuery((q) => ({ ...q, page: 1 })), []);
  return {
    query, rows, total, loading, error, reload, reloadSilent, resetPage, setRows, setTotal, setQuery,
    tableProps: { rows, total, loading, error, query, onQueryChange: setQuery, onRetry: reload, onRefresh: reload },
  };
}

/** sortParams — DataTable sort → { sort, order } query params (with a default) */
export function sortParams(query, fallbackKey = 'createdAt', fallbackOrder = 'desc') {
  if (!query?.sort?.key) return { sort: fallbackKey, order: fallbackOrder };
  return { sort: query.sort.key, order: query.sort.dir === -1 ? 'desc' : 'asc' };
}

/**
 * MultiCheck — multiple choice as a checklist (with a filter box for long lists).
 *   options: [{ value, label, hint }] | string[]   value: array   onChange(nextArray)
 */
export function MultiCheck({ options = [], value = [], onChange, emptyText = 'אין אפשרויות', maxHeight = 220, searchable }) {
  const [q, setQ] = useState('');
  const opts = options.map((o) => (typeof o === 'object' ? o : { value: o, label: o }));
  const showSearch = searchable ?? opts.length > 8;
  const shown = q ? opts.filter((o) => String(o.label).includes(q)) : opts;
  const set = new Set(value);
  const toggle = (v) => onChange?.(set.has(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <div className="nx-multi">
      {showSearch ? (
        <input className="ck-input ck-input--sm nx-multi__q" placeholder="סינון…" value={q} onChange={(e) => setQ(e.target.value)} />
      ) : null}
      <div className="nx-multi__list" style={{ maxHeight }}>
        {shown.length ? shown.map((o) => (
          <label key={String(o.value)} className={`nx-multi__row ${set.has(o.value) ? 'is-on' : ''}`}>
            <input type="checkbox" className="ck-checkbox" checked={set.has(o.value)} onChange={() => toggle(o.value)} />
            <span><span>{o.label}</span>{o.hint ? <small>{o.hint}</small> : null}</span>
          </label>
        )) : <span className="ck-muted" style={{ padding: 8 }}>{emptyText}</span>}
      </div>
      {value.length ? <small className="nx-multi__count">{`נבחרו ${value.length}`}</small> : null}
    </div>
  );
}
